// Logger abstraction — forwards to Better Stack telemetry on Vercel,
// falls back to console anywhere else (local dev, CI). This file is
// exempted from `noConsole` via a biome.json override because it IS the
// console abstraction layer.
//
// ─── Routing ───────────────────────────────────────────────────────────
// Three branches based on runtime + environment:
//
//   1. Browser  + Vercel  → `sendLog` + `reportError` (client.ts)
//                            → Better Stack JS tag in the user's browser
//   2. Server   + Vercel  → emitter registered via
//                            `registerServerLogEmitter` (called from
//                            `instrumentation.node.ts` after `server-log`
//                            wires itself up to the OTel LoggerProvider).
//                            If nothing's registered (e.g. boot order
//                            edge cases), the call is a silent no-op.
//   3. Anywhere not on Vercel → console.{info,warn,error}
//                                (local dev / CI)
//
// IMPORTANT: this module MUST stay client-safe. Statically importing
// `./server-log` here used to drag `next/headers` into client bundles
// via Turbopack's dependency analysis (it follows even dynamic imports)
// and broke the build. The registry pattern keeps server-log out of any
// import graph that touches a client component.

import { reportError, sendLog } from '@/lib/telemetry';
import { scrubGrantTokens } from '@/lib/telemetry/scrub';

// Use `NEXT_PUBLIC_VERCEL_URL` (inlined into the client bundle) rather
// than `process.env.VERCEL` (server-only). Otherwise, on Vercel the
// browser-side `logger.warn` / `logger.error` calls would silently fall
// through to `console.*` instead of routing to Better Stack via
// `sendLog` / `reportError`. The server branch is unaffected — both
// vars are populated on Vercel server-side.
const ON_VERCEL: boolean = Boolean(process.env.NEXT_PUBLIC_VERCEL_URL);

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/** Serialize context for dev console so Turbopack doesn't collapse objects to `{}`. */
function devContext(context: Record<string, unknown> | undefined): string {
  if (context === undefined) return '';
  try {
    return JSON.stringify(context);
  } catch {
    // SILENT-CATCH: logger serialization must not throw
    return String(context);
  }
}

interface Logger {
  info(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string, context?: Record<string, unknown>): void;
}

export type DebugLogger = (message: string, context?: Record<string, unknown>) => void;

/**
 * Namespaced debug logger gated to local dev. No-op on Vercel — these
 * are noisy flow traces (e.g. gift send/claim lifecycle), NOT telemetry
 * signals, so they never reach Better Stack regardless of environment.
 *
 *   const giftLog = makeDebugLogger('gifts');
 *   giftLog('send/usdc: submit() called', { senderAddress, amountD6 });
 */
export function makeDebugLogger(namespace: string): DebugLogger {
  if (ON_VERCEL) {
    return function noop(): void {
      // No-op on Vercel.
    };
  }
  return function debug(message: string, context?: Record<string, unknown>): void {
    const formatted = `[${namespace}] ${message}`;
    if (context === undefined) {
      console.log(formatted);
    } else {
      console.log(formatted, devContext(context));
    }
  };
}

// ─── Server emitter registry ──────────────────────────────────────────
// `instrumentation.node.ts` imports `./server-log` and calls
// `registerServerLogEmitter(emitServerLog)` on boot. Until then (or in
// any non-Node runtime that doesn't run instrumentation), server-side
// `logger.*` calls are no-ops.
type ServerEmitter = (
  level: 'info' | 'warn' | 'error',
  message: string,
  context: Record<string, unknown> | undefined,
) => void | Promise<void>;

let serverEmitter: ServerEmitter | null = null;

export function registerServerLogEmitter(emitter: ServerEmitter): void {
  serverEmitter = emitter;
}

function fireServerLog(
  level: 'info' | 'warn' | 'error',
  message: string,
  context: Record<string, unknown> | undefined,
): void {
  const emitter = serverEmitter;
  if (emitter === null) return;
  try {
    const result = emitter(level, message, context);
    if (result instanceof Promise) {
      result.catch(() => {
        // SILENT-CATCH: telemetry must never crash the service
      });
    }
  } catch {
    // SILENT-CATCH: telemetry must never crash the service
  }
}

// Scrub grant tokens from every log line before they hit a transport.
// Spec 048 FR-035..FR-038/FR-067: defence-in-depth — even though the
// impersonation client never logs the raw token, scrubbing here makes
// it impossible for an accidental log line carrying the token (e.g.
// from a future debug branch) to leak.
function scrubLog(
  message: string,
  context: Record<string, unknown> | undefined,
): { message: string; context: Record<string, unknown> | undefined } {
  return {
    message: scrubGrantTokens(message),
    context: context !== undefined ? scrubGrantTokens(context) : undefined,
  };
}

export const logger: Logger = {
  info(message: string, context?: Record<string, unknown>): void {
    const scrubbed = scrubLog(message, context);
    if (ON_VERCEL && isBrowser()) {
      sendLog(scrubbed.message, 'info', scrubbed.context);
    } else if (ON_VERCEL) {
      fireServerLog('info', scrubbed.message, scrubbed.context);
    } else {
      console.info(`[INFO] ${scrubbed.message}`, devContext(scrubbed.context));
    }
  },
  warn(message: string, context?: Record<string, unknown>): void {
    const scrubbed = scrubLog(message, context);
    if (ON_VERCEL && isBrowser()) {
      sendLog(scrubbed.message, 'warn', scrubbed.context);
    } else if (ON_VERCEL) {
      fireServerLog('warn', scrubbed.message, scrubbed.context);
    } else {
      console.warn(`[WARN] ${scrubbed.message}`, devContext(scrubbed.context));
    }
  },
  error(message: string, context?: Record<string, unknown>): void {
    const scrubbed = scrubLog(message, context);
    if (ON_VERCEL && isBrowser()) {
      sendLog(scrubbed.message, 'error', scrubbed.context);
      // Also report as a proper error with stack trace to Better Stack Errors.
      // This ensures every logger.error() produces a deobfuscated stack trace,
      // not just a text event.
      if (scrubbed.message !== 'tx.lifecycle') {
        reportError(new Error(scrubbed.message), { source: 'logger' });
      }
    } else if (ON_VERCEL) {
      fireServerLog('error', scrubbed.message, scrubbed.context);
    } else {
      console.error(`[ERROR] ${scrubbed.message}`, devContext(scrubbed.context));
    }
  },
};
